import { useFetcher } from "@remix-run/react";
import {
  Loader2,
  User2,
  Image as ImageIcon,
  Car,
  Calendar,
  MessageSquare,
} from "lucide-react";
import * as React from "react";
import {
  Avatar,
  AvatarFallback,
  AvatarImage,
} from "#app/components/ui/avatar.tsx";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "#app/components/ui/dialog.tsx";
import { getUserImgSrc } from "#app/utils/misc.tsx";

interface UserCarInfo {
  subtypeName: string | null; // Version/Trim
  userCar: {
    make: string;
    model: string;
  };
}

interface UserProfileData {
  id: string;
  username: string | null;
  createdAt: string;
  image: { id: string } | null;
  reviewCount: number;
  stationImageCount: number;
  userCarCount: number;
  userCars: UserCarInfo[]; // Add the user cars array
}

interface UserProfileDialogProps {
  userId: string;
  trigger: React.ReactNode;
  initialUsername?: string | null;
  initialImageId?: string | null;
}

export function UserProfileDialog({
  userId,
  trigger,
  initialUsername = "Usuário",
  initialImageId,
}: UserProfileDialogProps) {
  const fetcher = useFetcher<UserProfileData>();
  const [isOpen, setIsOpen] = React.useState(false);

  React.useEffect(() => {
    // Fetch data only when the dialog is open and data hasn't been fetched yet
    if (isOpen && fetcher.state === "idle" && !fetcher.data) {
      fetcher.load(`/api/user-profile/${userId}`);
    }
  }, [isOpen, userId, fetcher]);

  const profileData = fetcher.data;
  const isLoading = fetcher.state !== "idle";

  const formattedJoinDate = profileData?.createdAt
    ? new Date(profileData.createdAt).toLocaleDateString("pt-BR", {
        day: "2-digit",
        month: "long",
        year: "numeric",
      })
    : "...";

  const username = profileData?.username ?? initialUsername;
  const imageId = profileData?.image?.id ?? initialImageId;

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent className="sm:max-w-md p-0">
        <div className="relative h-24 bg-gradient-to-r from-blue-500 to-cyan-400 dark:from-blue-700 dark:to-cyan-600">
          {/* Placeholder for a potential cover image */}
        </div>
        <div className="relative -mt-12 flex justify-center">
          <Avatar className="h-24 w-24 border-4 border-background bg-background">
            {imageId ? (
              <AvatarImage
                src={getUserImgSrc(imageId)}
                alt={username ?? "Avatar do usuário"}
                className="object-cover"
              />
            ) : (
              <AvatarFallback className="bg-muted">
                <User2 className="h-12 w-12 text-muted-foreground" />
              </AvatarFallback>
            )}
          </Avatar>
        </div>

        <DialogHeader className="items-center text-center pt-2 pb-6 px-6 border-b">
          <DialogTitle className="text-2xl font-semibold">
            {username}
          </DialogTitle>
          {isLoading ? (
            <Loader2 className="h-4 w-4 animate-spin text-muted-foreground mt-1" />
          ) : (
            <p className="text-sm text-muted-foreground flex items-center gap-1 mt-1">
              <Calendar className="h-3.5 w-3.5" /> Membro desde{" "}
              {formattedJoinDate}
            </p>
          )}
        </DialogHeader>

        <div className="p-6 grid grid-cols-3 gap-4 text-center border-b">
          {isLoading ? (
            <div className="col-span-3 flex justify-center py-4">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : profileData ? (
            <>
              <StatItem
                icon={MessageSquare}
                value={profileData.reviewCount}
                label="Check-ins"
              />
              <StatItem
                icon={ImageIcon}
                value={profileData.stationImageCount}
                label="Fotos"
              />
              <StatItem
                icon={Car}
                value={profileData.userCarCount}
                label="Garagem"
              />
            </>
          ) : (
            <p className="col-span-3 text-sm text-destructive text-center">
              Não foi possível carregar os dados.
            </p>
          )}
        </div>

        {/* Garage Section */}
        <div className="p-6">
          <h4 className="text-md font-semibold mb-4 flex items-center gap-2 text-foreground">
            <Car className="h-5 w-5" />
            Garagem ({isLoading ? "..." : (profileData?.userCarCount ?? 0)})
          </h4>
          {isLoading ? (
            <div className="flex justify-center py-2">
              <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
            </div>
          ) : profileData && profileData.userCars.length > 0 ? (
            <ul className="space-y-2 max-h-40 overflow-y-auto pr-2">
              {profileData.userCars.map((car, index) => (
                <li
                  key={index}
                  className="text-sm text-muted-foreground flex items-center gap-1.5"
                >
                  <span className="font-medium text-foreground">
                    {car.userCar.make} {car.userCar.model}
                  </span>
                  {car.subtypeName &&
                    car.subtypeName.toLowerCase() !== "none" && (
                      <span className="text-xs">({car.subtypeName})</span>
                    )}
                </li>
              ))}
            </ul>
          ) : profileData && profileData.userCars.length === 0 ? (
            <p className="text-sm text-muted-foreground italic text-center py-2">
              Nenhum carro na garagem.
            </p>
          ) : (
            !isLoading && (
              <p className="text-sm text-destructive text-center py-2">
                Erro ao carregar garagem.
              </p>
            )
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

// Helper component for stats
function StatItem({
  icon: Icon,
  value,
  label,
}: {
  icon: React.ElementType;
  value: number;
  label: string;
}) {
  return (
    <div className="flex flex-col items-center gap-1">
      <Icon className="h-5 w-5 text-muted-foreground mb-1" />
      <p className="text-xl font-semibold">{value}</p>
      <p className="text-xs text-muted-foreground uppercase tracking-wider">
        {label}
      </p>
    </div>
  );
}
